import {
  ApiGeneralDataResponse,
  ApiResponse,
  BaseFollowSignalData,
  FiltersData,
  FiltersSlice,
  FilterType,
  FollowSignalData,
  GetSignalsRequest,
  LineAnnotation,
  MarginTypes,
  OneClickFollowBotData,
  OrderStatus,
  PrecisionConfig,
  ProcessedSignalTargets,
  RawTargetZone,
  RawZippedSignal,
  Signal,
  SignalExchange,
  SignalExtraDataResponse,
  SignalExtraInfoTuple,
  SignalGeneralExtraData,
  SignalProcessedGeneralData,
  SymbolClientsData,
  TargetCardData,
} from "@shared/types";
import { groupBy, isArray, mapValues } from "lodash";
import { convertToObjectsArray } from "@shared/utils/arrays";
import { OrderTypes } from "@shared/features/trades/common/types";
import {
  getAnnotationDistanceData,
  getClosestOrderAnnotation,
  getOrdersDefaultSortOrder,
  getTradeClosestOrder,
} from "@shared/features/trades/common/utils";
import { sortByNumberProperty } from "@shared/utils/general";
import {
  EditTargetData,
  EditTargetOrZone,
  EditTargetZone,
  TargetZoneCardData,
} from "@shared/features/signals/EditSignal/types";
import { getLineAnnotation } from "@shared/utils/charts";
import { round } from "@shared/utils/numbers";
import { AppThemeType } from "@shared/common/Themes/AppTheme";
import {
  testFollowStatusFilter,
  testNumberListFilter,
  testRangeFilter,
  testStringListFilter,
} from "@shared/features/filters/utils";
import {
  MARGIN_TYPE_TO_DISPLAY,
  TV_CHART_ORDER_TYPE_TO_DISPLAY_NAME,
} from "@shared/common/constants";
import {
  adminClosedSignalsFilters,
  adminOpenSignalsFilters,
  userClosedSignalsFilters,
  userOpenSignalsFilters,
} from "@shared/slices/filters.slice";
import { FollowSignalType } from "@shared/features/signals/common/types";
import { FOLLOW_SIGNAL_DATA_KEYS } from "@shared/features/signals/common/constants";

export const zipSignals = (
  response: ApiResponse<ApiGeneralDataResponse<Record<string, SignalExchange>>>,
  args: GetSignalsRequest
) =>
  Object.entries(response.data.data)
    .map(([exchange, { signal_cards }]) => {
      const rawSignals: RawZippedSignal[] = convertToObjectsArray(
        signal_cards,
        response.data?.headers
      );
      return rawSignals.map((signal) => {
        const signalExtraData = Object.fromEntries<SignalExtraInfoTuple>(
          signal.signal_extra_data
        );
        return {
          ...signal,
          exchange,
          exchanges: signalExtraData.Exchanges.split(", "),
          signal_extra_data: signalExtraData,
          sub_group_id: args.sub_group_id,
          is_open: args.is_open,
        };
      });
    }, {})
    .flat();

export const processSignalGeneralData = (
  data: SignalExtraDataResponse
): SignalGeneralExtraData => {
  const generalInfoTuples = data.general;
  const signalGeneralDataDictionary = generalInfoTuples.reduce(
    (acc: Record<string, string | number>, [name, value]) => {
      acc[name] = value;
      return acc;
    },
    {}
  ) as SignalProcessedGeneralData;

  return {
    ...data,
    graph_data: data.graph_data,
    precision: data.precision,
    general: signalGeneralDataDictionary,
    exchange_signal_cards: mergeExchangeSignalsFollowData(
      Object.values(data.exchange_signal_cards || {})
    ),
    is_open: !Boolean(signalGeneralDataDictionary["Closed at"]),
  };
};

const mergeFollowData = (
  baseFollowData?: BaseFollowSignalData,
  otherFollowData?: BaseFollowSignalData
) => {
  return FOLLOW_SIGNAL_DATA_KEYS.reduce((acc, key) => {
    acc[key] = [
      ...(baseFollowData?.[key] || []),
      ...(otherFollowData?.[key] || []),
    ];
    return acc;
  }, {} as BaseFollowSignalData);
};

export const mergeExchangeSignalsFollowData = (
  exchangeSignals: BaseFollowSignalData[]
) => {
  const [firstSignalInGroup] = exchangeSignals;
  return exchangeSignals.reduce((acc, current) => {
    return {
      ...current,
      ...mergeFollowData(acc, current),
    };
  }, firstSignalInGroup);
};

export const groupExchangeSignals = (signals: Signal[]) => {
  const groupedSignals = groupBy(signals, (signal) => signal.signal_id);
  return Object.values(groupedSignals).map(
    mergeExchangeSignalsFollowData
  ) as Signal[];
};

const mergeExchangeSignalsGeneralData = (exchangeSignals: Signal[]) => {
  const [firstSignal] = exchangeSignals;
  return exchangeSignals.reduce((acc, current) => {
    return {
      ...current,
      graph_data: {
        ...acc.graph_data,
        profit: Math.max(acc.graph_data.profit, current.graph_data.profit),
      },
      relevant_time: Math.max(acc.relevant_time, current.relevant_time),
    };
  }, firstSignal);
};

export const groupFilteredExchangeSignals = (
  signals: Signal[],
  signalIdToFollowData: Record<number, BaseFollowSignalData>
): Signal[] => {
  const groupedSignals = groupBy(signals, (signal) => signal.signal_id);
  return Object.values(groupedSignals).map((exchangeSignals) => {
    const signalData = mergeExchangeSignalsGeneralData(exchangeSignals);
    return { ...signalData, ...signalIdToFollowData[signalData.signal_id] };
  });
};

const convertTargetsCardsToEditTargets = (
  targetsCards: (TargetCardData | TargetZoneCardData)[],
  targetType: OrderTypes,
  isShort?: boolean,
  isBreakout?: boolean
): EditTargetOrZone[] => {
  const sortingOrder = getOrdersDefaultSortOrder(
    targetType,
    Boolean(isShort),
    Boolean(isBreakout)
  );
  return targetsCards
    .map((targetCard) => createEditableTarget(targetCard))
    .sort((a, b) => {
      return isEditableTargetZone(a)
        ? sortByNumberProperty(
            a.target_price,
            b.target_price,
            "[0]",
            sortingOrder
          )
        : sortByNumberProperty(a, b, "target_price", sortingOrder);
    });
};

const getEditableTargets = (
  cards: TargetCardData[],
  zones: RawTargetZone[],
  type: OrderTypes,
  isShort?: boolean,
  isBreakout?: boolean
) => {
  const hasTargets = cards.length > 0;
  if (hasTargets)
    return convertTargetsCardsToEditTargets(cards, type, isShort, isBreakout);
  return convertTargetsCardsToEditTargets(
    buildZoneCards(zones),
    type,
    isShort,
    isBreakout
  );
};

export const getFormattedTargets = (
  targets: ProcessedSignalTargets | undefined,
  isShort?: boolean,
  isBreakout?: boolean
) => {
  if (!targets) return;

  const entryTargets = getEditableTargets(
    targets?.entry_target_cards,
    targets?.entry_zones!,
    OrderTypes.ENTRY,
    isShort,
    isBreakout
  );
  const takeProfitTargets = getEditableTargets(
    targets.take_profit_target_cards,
    targets.take_profit_zones!,
    OrderTypes.TAKE_PROFIT,
    isShort,
    isBreakout
  );
  const stopTargets = getEditableTargets(
    targets?.stop_target_cards,
    targets?.stop_zones!,
    OrderTypes.STOP,
    isShort,
    isBreakout
  );

  return {
    entryTargets,
    takeProfitTargets,
    stopTargets,
  };
};

export const isEditableTargetZone = (
  target?: EditTargetZone | EditTargetData
): target is EditTargetZone => {
  return Array.isArray((target as EditTargetZone)?.edit_target_price);
};

export const createEditableTarget = (
  target: TargetCardData | TargetZoneCardData
): EditTargetOrZone => {
  if (isTargetZone(target)) {
    return {
      ...target,
      edit_target_price: [...target.target_price],
      edit_status: target.status,
    };
  }

  return {
    ...target,
    edit_target_price: target.target_price,
    edit_ratio: target.ratio,
    edit_status: target.status,
  };
};

export const isTargetZone = (
  target?: TargetCardData | TargetZoneCardData
): target is TargetZoneCardData => {
  return Array.isArray((target as TargetZoneCardData).target_price);
};

export const buildZoneCards = (
  zones: RawTargetZone[]
): Omit<EditTargetZone, "edit_status" | "edit_target_price">[] => {
  return zones
    .filter((zone) => !!zone)
    .map((zone) => ({
      target_price: [zone.min, zone.max],
      ratio: zone.ratio,
      status: zone.status as OrderStatus,
      price_reached: zone.price_reached,
    }));
};

export const getFollowSignalData = (
  signalGeneralData: SignalGeneralExtraData
): FollowSignalData => {
  const { symbol, is_open, sub_group_id, signal_id } = signalGeneralData || {};
  return {
    symbol,
    is_open,
    sub_group_id,
    signal_id,
    ...signalGeneralData.exchange_signal_cards,
  };
};

const getZoneText = (orderType: OrderTypes, isMin?: boolean) =>
  `${isMin ? "Bottom" : "Top"} ${
    TV_CHART_ORDER_TYPE_TO_DISPLAY_NAME[orderType]
  } Zone`;

const getLinePrices = (
  precisionConfig: PrecisionConfig,
  target: EditTargetOrZone,
  color: string,
  orderType: OrderTypes,
  isEdit?: boolean,
  currentPrice?: number,
  isSimpleLine?: boolean
) => {
  const precision = precisionConfig.price || precisionConfig.default || 0;
  const targetPrice = isEdit ? target.edit_target_price : target.target_price;
  if (!Array.isArray(targetPrice)) {
    const yValue = round(targetPrice, precision);
    return getLineAnnotation({ yMax: yValue, color, isSimpleLine, orderType });
  } else {
    const [min, max] = targetPrice;
    const yMinValue = round(min, precision);
    const yMaxValue = round(max, precision);
    const { distanceText: minValueDistance } = getAnnotationDistanceData(
      yMinValue,
      currentPrice
    );
    const { distanceText: maxValueDistance } = getAnnotationDistanceData(
      yMaxValue,
      currentPrice
    );
    return [
      getLineAnnotation({
        text: getZoneText(orderType, true),
        yMax: yMinValue,
        color,
        distance: minValueDistance,
        orderType,
      }),
      getLineAnnotation({
        text: getZoneText(orderType),
        yMax: yMaxValue,
        color,
        distance: maxValueDistance,
        orderType,
      }),
    ];
  }
};

export const getTargetsAnnotations = (
  theme: AppThemeType,
  entryTargets: EditTargetOrZone[],
  takeProfitTargets: EditTargetOrZone[],
  stopTargets: EditTargetOrZone[],
  precisionConfig: PrecisionConfig,
  isEdit?: boolean,
  currentPrice?: number
) => {
  const getTargetsAnnotation = (
    targets: EditTargetOrZone[],
    color: string,
    orderType: OrderTypes
  ) => {
    const isZone =
      targets.length === 1 &&
      isArray(targets[0][isEdit ? "edit_target_price" : "target_price"]);

    let filteredTargets = targets.filter((target) => {
      const status = target.status.toLowerCase();
      return (
        status === OrderStatus.PARTIALLY_FULFILLED.toLowerCase() ||
        status === OrderStatus.PENDING.toLowerCase()
      );
    });

    let closestOrderAnnotation;
    if (!isZone) {
      const closestOrder = getTradeClosestOrder(
        filteredTargets as EditTargetData[]
      );
      closestOrderAnnotation = closestOrder
        ? getClosestOrderAnnotation(
            closestOrder,
            orderType,
            color,
            precisionConfig.price,
            currentPrice
          )
        : undefined;
      filteredTargets = filteredTargets.filter(
        (target) => "id" in target && target.id !== closestOrder?.id
      );
    }

    const annotations: LineAnnotation[] = filteredTargets
      .map((target) =>
        getLinePrices(
          precisionConfig,
          target,
          color,
          orderType,
          isEdit,
          currentPrice,
          true
        )
      )
      .flat();
    if (closestOrderAnnotation) annotations.push(closestOrderAnnotation);
    return annotations;
  };

  return [
    ...getTargetsAnnotation(entryTargets, theme.colors.green, OrderTypes.ENTRY),
    ...getTargetsAnnotation(
      takeProfitTargets,
      theme.colors.red,
      OrderTypes.TAKE_PROFIT
    ),
    ...getTargetsAnnotation(stopTargets, theme.colors.orange, OrderTypes.STOP),
  ];
};

const testSignalExchangesFilter = (
  signal: Signal,
  filterExchanges: Set<string>
) => {
  return !!signal.exchange && filterExchanges.has(signal.exchange);
};

export const filterExchangeSignals = (
  signals: Signal[] | undefined,
  filters: FiltersData
) => {
  const filterExchangesSet = new Set(
    filters[FilterType.EXCHANGES]?.value || []
  );
  return signals?.filter((signalData) => {
    const signalExtraData = signalData.signal_extra_data;
    const marginType =
      MARGIN_TYPE_TO_DISPLAY[
        signalExtraData["Margin Type"] || MarginTypes.ISOLATED
      ];

    return (
      testSignalExchangesFilter(signalData, filterExchangesSet) &&
      (!signalExtraData["Entry Zone Category"] ||
        testNumberListFilter(
          filters[FilterType.SIGNAL_STATUS]?.value,
          signalExtraData["Entry Zone Category"]
        )) &&
      testStringListFilter(
        filters[FilterType.LONG_SHORT]?.value,
        signalExtraData["Long/Short"]
      ) &&
      testStringListFilter(
        filters[FilterType.MARGIN_TYPE]?.value,
        marginType
      ) &&
      testRangeFilter(
        filters[FilterType.TIME_FRAME]?.value?.range,
        signalData.relevant_time
      ) &&
      testRangeFilter(
        filters[FilterType.LEVERAGE]?.value,
        signalExtraData["Leverage"] || 1
      )
    );
  });
};

export const filterGroupedSignals = (
  signals: Signal[] | undefined,
  filters: FiltersData
) => {
  return signals?.filter((signalData) => {
    return testFollowStatusFilter(
      filters[FilterType.FOLLOW_STATUS]?.value,
      signalData
    );
  });
};

export const getSignalsFiltersSlice = (isOpen?: boolean, isAdmin?: boolean) => {
  let filters: FiltersSlice;
  if (isOpen) {
    if (isAdmin) filters = adminOpenSignalsFilters;
    else filters = userOpenSignalsFilters;
  } else {
    if (isAdmin) filters = adminClosedSignalsFilters;
    else filters = userClosedSignalsFilters;
  }
  return filters;
};

export const getListItemFollowData = (
  signal?: FollowSignalData,
  account?: SymbolClientsData,
  bot?: OneClickFollowBotData
) => {
  const objectId = bot?.id ?? account?.id;
  const fields = {
    allowFollow: new Set(bot ? signal?.bot_allow_follow : signal?.allow_follow),
    tradeExists: new Set(bot ? signal?.bot_trade_exists : signal?.trade_exists),
    requiresUpdate: new Set(
      bot ? signal?.bot_requires_update : signal?.requires_update
    ),
    requiresCancel: new Set(
      bot ? signal?.bot_requires_cancel : signal?.requires_cancel
    ),
  };
  return mapValues(
    fields,
    (objectIds) => !!objectId && objectIds?.has(objectId)
  );
};

export const getSignalFollowType = (
  followData?: BaseFollowSignalData,
  accountId?: number,
  botId?: number
) => {
  const isGeneralCheck = !botId && !accountId;
  const isValidType = (
    botKey: keyof BaseFollowSignalData,
    accountKey: keyof BaseFollowSignalData
  ) => {
    const botIds = followData?.[botKey];
    const accountIds = followData?.[accountKey];
    if (isGeneralCheck) return !!botIds?.length || !!accountIds?.length;
    else if (botId) return botIds?.includes(botId);
    else if (accountId) return accountIds?.includes(accountId);
  };

  const isFollowingSignal = isValidType("bot_trade_exists", "trade_exists");
  const requiresUpdate = isValidType("bot_requires_update", "requires_update");
  const requiresCancel = isValidType("bot_requires_cancel", "requires_cancel");

  if (requiresCancel) return FollowSignalType.CANCEL;
  else if (requiresUpdate) return FollowSignalType.UPDATE;
  else if (isFollowingSignal) return FollowSignalType.FOLLOW;
};

export const getSignalsFilterTypeToTitle = (isOpen: boolean) => ({
  [FilterType.TIME_FRAME]: isOpen
    ? "Signal Open/Update Time"
    : "Signal Close Time",
});
